import { createElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import Actions from '@/components/Menu/Actions';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useGlobalStore } from '@/state/global.state';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { WarningCircleIcon } from '@/UI/Icons/WarningCircleIcon';
import { useNodeAssetManager } from '../FlowContext';
import ParentMenuItem from './ParentMenuItem';
import type { MenuAction } from '@/components/Menu/Actions';
import type { Node } from '@/types/node';

interface ClearAssetsSubmenuProps {
  nodes: Node[];
  onClose: () => void;
  clearAllLabel: string;
}

export function ClearAssetsSubmenu({ nodes, onClose, clearAllLabel }: ClearAssetsSubmenuProps) {
  const { t } = useTranslation();
  const nodeAssetManager = useNodeAssetManager();
  const { openConfirmationDialog } = useConfirmationDialogStore();
  const { updateSnackbarData } = useGlobalStore();
  const { getItem: getSuppressClearAllConfirmation, setItem: setSuppressClearAllConfirmation } =
    useLocalStorage<boolean>('suppressClearAllAssetsConfirmation');
  const { getItem: getSuppressClearOtherConfirmation, setItem: setSuppressClearOtherConfirmation } =
    useLocalStorage<boolean>('suppressClearOtherAssetsConfirmation');

  const hasClearable = useMemo(() => nodeAssetManager.hasClearableAssets(nodes), [nodeAssetManager, nodes]);
  const hasClearableOthers = useMemo(() => nodeAssetManager.hasClearableOtherAssets(nodes), [nodeAssetManager, nodes]);

  const showClearError = useCallback(() => {
    updateSnackbarData({
      text: t(I18N_KEYS.RECIPE_MAIN.ERRORS.CLEAR_CONTENT_FAILED),
      isOpen: true,
      severity: 'error',
      icon: createElement(WarningCircleIcon, { width: 20, height: 20 }),
      action: null,
      duration: 5000,
    });
  }, [updateSnackbarData, t]);

  const clearAllAssets = useCallback(() => {
    try {
      nodeAssetManager.clearAssetsFromNodes(nodes);
    } catch {
      showClearError();
    } finally {
      onClose();
    }
  }, [nodeAssetManager, nodes, onClose, showClearError]);

  const handleClearAllAssets = useCallback(() => {
    if (getSuppressClearAllConfirmation()) {
      clearAllAssets();
      return;
    }
    openConfirmationDialog({
      title: t(I18N_KEYS.RECIPE_MAIN.FLOW.CLEAR_ALL_CONTENT_CONFIRMATION.TITLE),
      message: t(I18N_KEYS.RECIPE_MAIN.FLOW.CLEAR_ALL_CONTENT_CONFIRMATION.MESSAGE),
      checkboxLabel: t(I18N_KEYS.RECIPE_MAIN.FLOW.CLEAR_ALL_CONTENT_CONFIRMATION.CHECKBOX_LABEL),
      isCheckboxChecked: getSuppressClearAllConfirmation() ?? false,
      onConfirm: (checkboxChecked) => {
        setSuppressClearAllConfirmation(checkboxChecked ?? false);
        clearAllAssets();
      },
    });
  }, [getSuppressClearAllConfirmation, setSuppressClearAllConfirmation, openConfirmationDialog, t, clearAllAssets]);

  const clearOtherAssets = useCallback(() => {
    try {
      nodeAssetManager.clearOtherAssetsFromNodes(nodes);
    } catch {
      showClearError();
    } finally {
      onClose();
    }
  }, [nodeAssetManager, nodes, onClose, showClearError]);

  const handleClearOtherAssets = useCallback(() => {
    const shouldSuppressConfirmation = getSuppressClearOtherConfirmation();
    if (shouldSuppressConfirmation) {
      clearOtherAssets();
      return;
    }
    openConfirmationDialog({
      title: t(I18N_KEYS.RECIPE_MAIN.FLOW.CLEAR_OTHER_CONTENT_CONFIRMATION.TITLE),
      message: t(I18N_KEYS.RECIPE_MAIN.FLOW.CLEAR_OTHER_CONTENT_CONFIRMATION.MESSAGE),
      checkboxLabel: t(I18N_KEYS.RECIPE_MAIN.FLOW.CLEAR_OTHER_CONTENT_CONFIRMATION.CHECKBOX_LABEL),
      isCheckboxChecked: shouldSuppressConfirmation ?? false,
      onConfirm: (checkboxChecked) => {
        setSuppressClearOtherConfirmation(checkboxChecked ?? false);
        clearOtherAssets();
      },
    });
  }, [
    getSuppressClearOtherConfirmation,
    setSuppressClearOtherConfirmation,
    openConfirmationDialog,
    t,
    clearOtherAssets,
  ]);

  const clearSubmenuItems: MenuAction[] = useMemo(
    () => [
      {
        name: t(I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.CLEAR_ALL_BUT_SELECTED),
        action: handleClearOtherAssets,
        disabled: !hasClearableOthers,
      },
      {
        name: clearAllLabel,
        action: handleClearAllAssets,
        disabled: !hasClearable,
      },
    ],
    [t, clearAllLabel, handleClearOtherAssets, handleClearAllAssets, hasClearableOthers, hasClearable],
  );

  return (
    <ParentMenuItem
      label={t(I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.CLEAR)}
      contextMenuWidth="180px"
      disabled={!hasClearable && !hasClearableOthers}
      onClose={onClose}
    >
      <Actions items={clearSubmenuItems} onClose={(_e) => onClose()} />
    </ParentMenuItem>
  );
}
